import React, { useState, useEffect } from 'react';
import './ProductCard.css';
import { TbShoppingBag } from "react-icons/tb";
import { MdOutlineRemoveRedEye } from "react-icons/md";
import { FaHeart, FaRegHeart, FaStar, FaStarHalfAlt, FaRegStar } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import { addToCart, setCart, setTotalItems, setTotalPrice, setCartId } from "../../store/cartSlice";
import { getWithoutTokenAPI,getWithTokenAPI, postWithTokenAPI } from '../../util/Api';
import ProductCardpop from '../Product-quick-view/quickview';
import { useNavigate } from 'react-router-dom';
import LoginPopup from '../Loginpopup/Loginpop';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { showToastSuccessMsg, showToastErrorMsg } from '../../store/toastMsg';
import { incrementWishlistCount, decrementWishlistCount, setWishlistCount, addToWishlist, removeFromWishlist } from '../../store/wishlistSlice';

// Define CustomStarRating component
const CustomStarRating = ({ rating, totalStars = 5 }) => {
    const fullStars = Math.floor(rating);
    const halfStar = rating % 1 >= 0.5 ? 1 : 0;
    const emptyStars = totalStars - fullStars - halfStar;

    return (
        <div className="custom-star-rating">
            {[...Array(fullStars)].map((_, i) => (
                <FaStar key={`full-${i}`} color="#ffd700" />
            ))}
            {halfStar === 1 && <FaStarHalfAlt key="half" color="#ffd700" />}
            {[...Array(emptyStars)].map((_, i) => (
                <FaRegStar key={`empty-${i}`} color="#d4d4d4" />
            ))}
        </div>
    );
};

const ProductCardimg = ({ product }) => {
    const navigate = useNavigate();
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [isLoginPopupOpen, setLoginPopupOpen] = useState(false);
    const [error, setError] = useState(null);
    const [addingToCart, setAddingToCart] = useState(false);
    const accessToken = useSelector((state) => state.auth?.auth?.access_token);
    const isLoggedIn = useSelector((state) => state.auth?.isLoggedIn);
    const userId = useSelector((state) => state.auth?.auth?._id);
    const wishlistItems = useSelector((state) => state.wishlist.items); // Get all wishlist items

    const dispatch = useDispatch();
    const [isInWishlist, setIsInWishlist] = useState(false);

    // Initialize and synchronize wishlist state with Redux store
    useEffect(() => {
        if (product) {
            if (isLoggedIn && wishlistItems) {
                const isActuallyInWishlist = wishlistItems.some(item => item._id === product._id);
                setIsInWishlist(isActuallyInWishlist);
            } else {
                // Fallback to API's wish_status if not logged in or wishlistItems not available
                setIsInWishlist(product.wish_status || false);
            }
        }
    }, [product, wishlistItems, isLoggedIn]); // Added wishlistItems and isLoggedIn to dependencies

    // Fetch wishlist count and update Redux store
    useEffect(() => {
        const fetchWishlistCount = async () => {
            if (!isLoggedIn || !userId) return;

            try {
                const response = await getWithTokenAPI(accessToken,`/wishlist/find-all?user_id=${userId}`);
                if (response?.statusCode === 200) {
                    const wishlistData = response.data?.wishlist || [];
                    // Update Redux store with count
                    dispatch(setWishlistCount(wishlistData.length));
                }
            } catch (error) {
                console.error("Error fetching wishlist count:", error);
            }
        };

        fetchWishlistCount();
    }, [isLoggedIn, userId, dispatch]);

    const handleWishlistClick = async (e) => {
        e.stopPropagation();

        if (!isLoggedIn || !accessToken) {
            setLoginPopupOpen(true);
            return;
        }

        try {
            const payload = {
                product_id: product._id,
                category_id: product.category_id || product.category?._id,
            };

            const response = await postWithTokenAPI(accessToken, "/wishlist/add", payload);

            if (response?.statusCode === 200) {
                // Update local state
                setIsInWishlist(true);
                dispatch(addToWishlist(product)); // Dispatch action to add item to Redux wishlist
                dispatch(incrementWishlistCount());
                showToastSuccessMsg('Item added to wishlist successfully!');
            } else if (response?.statusCode === 406) {
                // Update local state
                setIsInWishlist(false);
                dispatch(removeFromWishlist(product._id)); // Dispatch action to remove item by ID from Redux wishlist
                
                // Fetch updated count from API to ensure accuracy
                try {
                    const countResponse = await getWithTokenAPI(accessToken,`/wishlist/find-all?user_id=${userId}`);
                    if (countResponse?.statusCode === 200) {
                        const wishlistData = countResponse.data?.wishlist || [];
                        dispatch(setWishlistCount(wishlistData.length));
                    }
                } catch (error) {
                    console.error("Error fetching updated wishlist count:", error);
                }
                
                showToastErrorMsg('Item removed from wishlist');
            } else {
                console.warn("Failed to update wishlist:", response);
                showToastErrorMsg('Failed to update wishlist');
            }
        } catch (error) {
            console.error("Error updating wishlist:", error);
            showToastErrorMsg('Error updating wishlist');
        }
    };

    if (!product) {
        console.warn("Product is undefined in ProductCardimg");
        return null;
    }

    const handleFindParticular = async (_id) => {
        try {
            const queryParams = userId && userId.length === 24 ? `&user_id=${userId}` : '';
            const response = await getWithoutTokenAPI(`/product/find-particular?_id=${_id}${queryParams}`);
            if (response && response.statusCode === 200) {
                const productData = response.data?.Product || response.data;
                // Update wishlist status based on API response
                setIsInWishlist(productData.wish_status || false);
                // Navigate with the wish_status included
                navigate(`/product?_id=${_id}&seller_name=${encodeURIComponent(productData.seller_id?.name || '')}&product_name=${encodeURIComponent(productData.name || '')}&wish_status=${productData.wish_status || false}`);
            } else {
                console.warn("Bad response:", response);
            }
        } catch (error) {
            console.error("Error fetching products:", error);
        }
    };

    const openPopup = async (_id) => {
        if (!_id) return;

        try {
            const queryParams = userId ? `&user_id=${userId}` : '';
            const response = await getWithoutTokenAPI(`/product/find-particular?_id=${_id}${queryParams}`);
            if (response && response.statusCode === 200 && response.data) {
                const productData = response.data?.Product || response.data;
                const productWithSeller = {
                    ...productData,
                    sellerName: productData.seller_id?.name || "Unknown Seller",
                    wish_status: productData.wish_status || false
                };
                setSelectedProduct(productWithSeller);
                setIsPopupOpen(true);
            } else {
                console.warn("Product not found or bad response:", response);
            }
        } catch (error) {
            console.error("Error fetching product:", error);
        }
    };

    const closePopup = () => {
        setIsPopupOpen(false);
        setSelectedProduct(null);
    };

    const closeLoginPopup = () => {
        setLoginPopupOpen(false);
    };

    // const handleAddToCart = async (cartItem) => {
    //     if (!accessToken) {
    //         setLoginPopupOpen(true);
    //         return;
    //     }
    //     if (!cartItem?._id) return;

    //     if (addingToCart) return;
    //     setAddingToCart(true);
    //     setError(null);

    //     try {
    //         const payload = {
    //             cart: [
    //                 {
    //                     product_id: cartItem._id,
    //                     quantity: cartItem.quantity > 0 ? cartItem.quantity : 1,
    //                 },
    //             ],
    //         };

    //         const response = await postWithTokenAPI(accessToken, "/cart/add", payload);

    //         // 🧹 Case: Cart emptied
    //         if (
    //             response?.statusCode === 200 &&
    //             response?.message === "Product removed and cart deleted as it became empty"
    //         ) {
    //             dispatch(setCart([]));
    //             dispatch(setTotalItems(0));
    //             dispatch(setTotalPrice(0));
    //             dispatch(setCartId(null));
    //             toast.info("Item removed and cart is now empty.", {
    //                 position: "top-right",
    //                 autoClose: 3000,
    //                 hideProgressBar: false,
    //                 closeOnClick: true,
    //                 pauseOnHover: true,
    //                 draggable: true,
    //                 toastId: `removed-${cartItem._id}`,
    //                 theme: "light",
    //             });
    //         }

    //         // ✅ Case: Product added successfully
    //         else if (response?.statusCode === 200 && response.data?.cart) {
    //             const addedItem = response.data.cart;

    //             // Prepare payload and ensure it's valid before dispatching
    //             const cartActionPayload = {
    //                 id: addedItem.product_id,
    //                 price: addedItem.price,
    //                 quantity: addedItem.quantity,
    //                 name: addedItem.name,
    //                 image: addedItem.product_image?.[0] || addedItem.image, // Prioritize product_image if available
    //             };

    //             if (cartActionPayload.id && cartActionPayload.name && typeof cartActionPayload.price !== 'undefined' && typeof cartActionPayload.quantity !== 'undefined') {
    //                 dispatch(addToCart(cartActionPayload));
    //             } else {
    //                 console.warn("ProductCard.jsx: Could not dispatch addToCart due to missing data from API response. Cart count might not update immediately.", addedItem);
    //                 // As a fallback, or if the API is guaranteed to have updated the backend count, 
    //                 // you could consider re-fetching the total cart count directly here,
    //                 // though ideally client-side state should be derivable.
    //                 // Example: dispatch(fetchTotalCartItemsFromServerThunk());
    //             }

    //             toast.success("Item added to cart successfully!", {
    //                 position: "top-right",
    //                 autoClose: 3000,
    //                 hideProgressBar: false,
    //                 closeOnClick: true,
    //                 pauseOnHover: true,
    //                 draggable: true,
    //                 toastId: `cart-${cartItem._id}`,
    //                 theme: "light",
    //             });
    //         }

    //         // ❌ Failure case
    //         else {
    //             toast.error("Failed to add item to cart.", {
    //                 position: "top-right",
    //                 autoClose: 3000,
    //                 hideProgressBar: false,
    //                 closeOnClick: true,
    //                 pauseOnHover: true,
    //                 draggable: true,
    //                 toastId: `error-${cartItem._id}`,
    //                 theme: "light",
    //             });
    //         }
    //     } catch (error) {
    //         console.error("Add to cart error:", error);
    //         toast.error("Something went wrong. Please try again.", {
    //             position: "top-right",
    //             autoClose: 3000,
    //             hideProgressBar: false,
    //             closeOnClick: true,
    //             pauseOnHover: true,
    //             draggable: true,
    //             toastId: `error-${cartItem._id}`,
    //             theme: "light",
    //         });
    //     } finally {
    //         setAddingToCart(false);
    //     }
    // };

    
    
    const handleAddToCart = async (cartItem) => {
        if (!accessToken) {
          setLoginPopupOpen(true);
          return;
        }
        if (!cartItem?._id) return;
    
        if (addingToCart) return;
        setAddingToCart(true);
        setError(null);
    
        try {
          const payload = {
            cart: [
              {
                product_id: cartItem._id,
                quantity: cartItem.quantity > 0 ? cartItem.quantity : 1,
              },
            ],
          };
    
          const response = await postWithTokenAPI(accessToken, "/cart/add", payload);
    
          // 🧹 Case: Cart emptied
          if (
            response?.statusCode === 200 &&
            response?.message === "Product removed and cart deleted as it became empty"
          ) {
            dispatch(setCart([]));
            dispatch(setTotalItems(0));
            dispatch(setTotalPrice(0));
            dispatch(setCartId(null));
            toast.info("Item removed and cart is now empty.", {
              position: "top-right",
              autoClose: 3000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              toastId: `removed-${cartItem._id}`,
              theme: "light",
            });
          }
    
          // ✅ Case: Product added successfully
          else if (response?.statusCode === 200 && response.data?.cart) {
            const addedItem = response.data.cart;
    
            dispatch(
              addToCart({
                id: addedItem.product_id,
                price: addedItem.price,
                quantity: addedItem.quantity,
                name: addedItem.name,
                image: addedItem.image,
              })
            );
    
            toast.success("Item added to cart successfully!", {
              position: "top-right",
              autoClose: 3000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              toastId: `cart-${cartItem._id}`,
              theme: "light",
            });
          }
    
          // ❌ Failure case
          else {
            toast.error("Failed to add item to cart.", {
              position: "top-right",
              autoClose: 3000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              toastId: `error-${cartItem._id}`,
              theme: "light",
            });
          }
        } catch (error) {
          console.error("Add to cart error:", error);
          toast.error("Something went wrong. Please try again.", {
            position: "top-right",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            toastId: `error-${cartItem._id}`,
            theme: "light",
          });
        } finally {
          setAddingToCart(false);
        }
      };
      
    return (
        <>
            {isLoginPopupOpen && <LoginPopup onClose={closeLoginPopup} />}

            <div className={`product-card-box${isInWishlist ? ' wishlist-active' : ''}`} key={product._id}>
                <div className="product-card-header" >
                    {product.sale ? (
                        <span className="product-card-first__sale-tag">{product.status}</span>
                    ) : (
                        <span className="product-card-first__notsale-tag"></span>
                    )}
                </div>

                <div
                    className="product-img-component"
                    style={{ backgroundImage: `url(${product.product_image[0]})` }}
                    onClick={() => handleFindParticular(product._id)}
                >
                    <div className="card-actions shoppage-wish-view">
                        <div
                            className={`card-icon-heart ${isInWishlist ? 'wishlist-active' : ''}`}
                            onClick={handleWishlistClick}
                            style={{
                                background: "white",
                                border: "1px solid #e6e6e6",
                                borderRadius: "50%",
                                padding: "6px",
                                width: "32px",
                                height: "32px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                                transition: "all 0.3s ease"
                            }}
                        >
                            {isInWishlist ? (
                                <FaHeart style={{ color: '#CC0000', fontSize: '16px' }} />
                            ) : (
                                <FaRegHeart style={{ color: '#1a1a1a', fontSize: '16px' }} />
                            )}
                        </div>
                        <MdOutlineRemoveRedEye
                            onClick={(e) => {
                                e.stopPropagation();
                                openPopup(product._id);
                            }}
                            className="card-icon action-btn--eye"
                            title="Quick View"
                        />
                    </div>
                </div>

                <div className="product-info-box">
                    <div className="product-name">{product.name}</div>
                    <span
                        className="ff-poppins fs-12"
                        style={{ color: "#CC0000" }}
                    >{product.sellerName}</span>
                    <div className="product-price-box">
                        <span className="current-price-box">
                            {product.discount_price ? `Ksh ${product.discount_price.toFixed(2)}` : 'Price not available'}
                        </span>
                        {product.original_price && (
                            <span className="old-price-box">Ksh {product.original_price.toFixed(2)}</span>
                        )}
                    </div>
                    <span className="product-quantity-box">
                        {/* Quantity : {product.quantity || product.varient} */}
                    </span>
                    <div className="review">
<div className='product-rating shoppage-product-rating'>
                        <CustomStarRating rating={product.average_rating || 0} />
                        </div>
                        <p>{product.store_name}</p>
                    </div>

                    <div className="action-row">
                        <div></div>
                        <div
                            className="tbshop"
                            title="Add to Cart"
                            onClick={() => handleAddToCart({ ...product, quantity: 1 })}
                        >
                            <TbShoppingBag className="TbShop" />
                        </div>
                    </div>
                </div>
            </div>

            {isPopupOpen && selectedProduct && (
                <ProductCardpop
                    isOpen={isPopupOpen}
                    onClose={closePopup}
                    selectedProductId={selectedProduct._id}
                    productDetails={selectedProduct}
                />
            )}
        </>
    );
};

export default ProductCardimg;
